"use client";

/**
 * Client-side analytics dual-fire helper.
 *
 * Fires the same event to GA4 (gtag) and Umami (umami.track). Umami is the
 * portfolio analytics standard; GA4 is kept for continuity.
 *
 * Both scripts load with `lazyOnload`, so window.gtag / window.umami may not
 * exist yet when an event fires (e.g. a sign_up firing right after a fresh
 * page load). gtag has its own dataLayer queue, but Umami does not — so we
 * queue Umami events and flush once window.umami appears, giving up after a
 * short window to avoid leaking timers.
 */

type EventProps = Record<string, string | number | boolean>;

interface GtagWindow {
  gtag?: (...args: unknown[]) => void;
  umami?: { track?: (name: string, data?: EventProps) => void };
}

function fireGtag(name: string, props?: EventProps) {
  if (typeof window === "undefined") return;
  const gtag = (window as unknown as GtagWindow).gtag;
  if (typeof gtag === "function") gtag("event", name, props ?? {});
}

const umamiQueue: Array<{ name: string; props?: EventProps }> = [];
let umamiPolling = false;

function getUmamiTrack() {
  if (typeof window === "undefined") return undefined;
  const umami = (window as unknown as GtagWindow).umami;
  return umami && typeof umami.track === "function" ? umami.track.bind(umami) : undefined;
}

function drainUmami() {
  const track = getUmamiTrack();
  if (!track) return;
  while (umamiQueue.length) {
    const ev = umamiQueue.shift()!;
    track(ev.name, ev.props);
  }
}

function fireUmami(name: string, props?: EventProps) {
  if (typeof window === "undefined") return;
  umamiQueue.push({ name, props });

  const track = getUmamiTrack();
  if (track) {
    drainUmami();
    return;
  }

  // Umami script not ready yet — poll until it loads, then flush. Give up after
  // ~10s (50 × 200ms) so we never leave a timer running indefinitely.
  if (umamiPolling) return;
  umamiPolling = true;
  let tries = 0;
  const interval = setInterval(() => {
    tries += 1;
    if (getUmamiTrack()) {
      clearInterval(interval);
      umamiPolling = false;
      drainUmami();
    } else if (tries >= 50) {
      clearInterval(interval);
      umamiPolling = false;
    }
  }, 200);
}

/** Fire an analytics event to both GA4 and Umami. Safe to call before either script loads. */
export function trackEvent(name: string, props?: EventProps) {
  fireGtag(name, props);
  fireUmami(name, props);
}
